import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useCandidateQuery } from "@/queries/candidate/candidate";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { GroupedMultiSelect } from "@/components/ui/multi-select";

import { candidateSkillFormSchema, CandidateSkillFormSchema } from "./schema";
import {
  dataAndAnalyticsSkillList,
  financeSkillList,
  hrSkillList,
  marketingSkillList,
  operationsSkillList,
  productSkillList,
  Role,
  salesSkillList,
  Skills,
  softwareEngineeringSkillList,
} from "@/redux/features/candidateProfileSlice";

import { useUpdateCandidateProfile } from "@/mutations/candidate/profile/updateProfile";

const CandidateCompetenciesEditFormDialog = ({
  profileId,
}: {
  profileId: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { candidate } = useCandidateQuery(profileId);
  const { updateCandidateProfile, isUpdatingCandidateProfile } =
    useUpdateCandidateProfile(profileId, () => setIsOpen(false));

  const form = useForm<CandidateSkillFormSchema>({
    resolver: zodResolver(candidateSkillFormSchema),
    mode: "onChange",
    defaultValues: {
      topSkills: candidate?.topSkills ?? [],
      secondarySkills: candidate?.secondarySkills ?? [],
      additionalSkills: candidate?.additionalSkills ?? [],
    },
  });

  const { topSkills, secondarySkills, additionalSkills } = form.watch();

  const { topOptions, secondaryOptions, additionalOptions, optionsCount } =
    useMemo(() => {
      return generateSkillOptionsLists({
        roles: candidate?.idealRoles ?? [],
        topSkills: topSkills ?? [],
        secondarySkills: secondarySkills ?? [],
        additionalSkills: additionalSkills ?? [],
      });
    }, [candidate?.idealRoles, topSkills, secondarySkills, additionalSkills]);

  useEffect(() => {
    form.reset({
      topSkills: candidate?.topSkills ?? [],
      secondarySkills: candidate?.secondarySkills ?? [],
      additionalSkills: candidate?.additionalSkills ?? [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, candidate]);

  const onSubmit = (data: CandidateSkillFormSchema) => {
    updateCandidateProfile({
      emails: candidate?.emails ?? [],
      ...data,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="link" className="edit-text self-end">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="w-full sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Edit Core Competencies</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Update your core competencies below.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
          <Form {...form}>
            <form
              className="space-y-4 w-full"
              onSubmit={(e) => {
                e.preventDefault();
                void form.handleSubmit((data) => onSubmit(data))();
              }}
            >
              <FormField
                control={form.control}
                name="topSkills"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Choose your 5 top skills out of {optionsCount}
                    </FormLabel>
                    <FormControl>
                      <GroupedMultiSelect
                        values={field.value ?? []}
                        setValues={(skills) => {
                          const currentSecondary =
                            form.getValues("secondarySkills") ?? [];
                          const currentAdditional =
                            form.getValues("additionalSkills") ?? [];

                          // Remove any overlaps from other categories
                          const filteredSecondary = currentSecondary.filter(
                            (skill) => !skills.includes(skill),
                          );
                          const filteredAdditional = currentAdditional.filter(
                            (skill) => !skills.includes(skill),
                          );

                          // Set all three values
                          form.setValue("topSkills", skills as Skills[]);
                          form.setValue("secondarySkills", filteredSecondary);
                          form.setValue("additionalSkills", filteredAdditional);
                        }}
                        groupedOptions={topOptions}
                        max={5}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="secondarySkills"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Choose your 5 secondary skills out of {optionsCount}
                    </FormLabel>
                    <FormControl>
                      <GroupedMultiSelect
                        values={field.value ?? []}
                        setValues={(skills) => {
                          const currentTop = form.getValues("topSkills") ?? [];
                          const currentAdditional =
                            form.getValues("additionalSkills") ?? [];

                          // Remove any overlaps from other categories
                          const filteredTop = currentTop.filter(
                            (skill) => !skills.includes(skill),
                          );
                          const filteredAdditional = currentAdditional.filter(
                            (skill) => !skills.includes(skill),
                          );

                          // Set all three values
                          form.setValue("topSkills", filteredTop);
                          form.setValue("secondarySkills", skills as Skills[]);
                          form.setValue("additionalSkills", filteredAdditional);
                        }}
                        groupedOptions={secondaryOptions}
                        max={5}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="additionalSkills"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Choose your 10 additional skills out of {optionsCount}
                    </FormLabel>
                    <FormControl>
                      <GroupedMultiSelect
                        values={field.value ?? []}
                        setValues={(skills) => {
                          const currentTop = form.getValues("topSkills") ?? [];
                          const currentSecondary =
                            form.getValues("secondarySkills") ?? [];

                          // Remove any overlaps from other categories
                          const filteredTop = currentTop.filter(
                            (skill) => !skills.includes(skill),
                          );
                          const filteredSecondary = currentSecondary.filter(
                            (skill) => !skills.includes(skill),
                          );

                          // Set all three values
                          form.setValue("topSkills", filteredTop);
                          form.setValue("secondarySkills", filteredSecondary);
                          form.setValue("additionalSkills", skills as Skills[]);
                        }}
                        groupedOptions={additionalOptions}
                        max={10}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="w-full flex justify-between">
                <Button
                  variant="outline"
                  className="w-fit"
                  type="button"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  className="w-fit"
                  type="submit"
                  disabled={
                    !form.formState.isValid || isUpdatingCandidateProfile
                  }
                >
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const generateSkillOptionsLists = ({
  roles,
  topSkills,
  secondarySkills,
  additionalSkills,
}: {
  roles: Role[];
  topSkills: Skills[];
  secondarySkills: Skills[];
  additionalSkills: Skills[];
}) => {
  const fallback =
    !roles.length || (roles.length === 1 && roles[0] === "Medical / Clinical");

  // Generate the skill option
  const skillOptionGenerator = (
    list: "top" | "secondary" | "additional",
    option: Skills,
  ) => {
    let label: string = option;

    if (list !== "top" && topSkills.includes(option)) {
      label = `${option} (Top)`;
    } else if (list !== "secondary" && secondarySkills.includes(option)) {
      label = `${option} (Secondary)`;
    } else if (list !== "additional" && additionalSkills.includes(option)) {
      label = `${option} (Additional)`;
    }

    return { label, value: option };
  };

  // Set the first parameter of the skillOptionGenerator for easier calling
  const topSkillOptionGenerator = (option: Skills) =>
    skillOptionGenerator("top", option);
  const secondarySkillOptionGenerator = (option: Skills) =>
    skillOptionGenerator("secondary", option);
  const additionalSkillOptionGenerator = (option: Skills) =>
    skillOptionGenerator("additional", option);

  const topOptions: {
    group: string;
    options: { label: string; value: string }[];
  }[] = [];
  const secondaryOptions: {
    group: string;
    options: { label: string; value: string }[];
  }[] = [];
  const additionalOptions: {
    group: string;
    options: { label: string; value: string }[];
  }[] = [];
  let optionsCount = 0;

  const addSkillGroup = (
    skills: readonly Skills[],
    groups: { group: string; from: number; to: number }[],
  ) => {
    topOptions.push(
      ...groups.map(({ group, from, to }) => ({
        group,
        options: skills.slice(from, to).map(topSkillOptionGenerator),
      })),
    );
    secondaryOptions.push(
      ...groups.map(({ group, from, to }) => ({
        group,
        options: skills.slice(from, to).map(secondarySkillOptionGenerator),
      })),
    );
    additionalOptions.push(
      ...groups.map(({ group, from, to }) => ({
        group,
        options: skills.slice(from, to).map(additionalSkillOptionGenerator),
      })),
    );

    optionsCount += skills.length;
  };

  // For each role, list all skills
  if (fallback || roles.includes("Finance")) {
    addSkillGroup(financeSkillList, [
      { group: "Strategic Financial Management", from: 0, to: 5 },
      { group: "Accounting & Compliance", from: 5, to: 10 },
      { group: "Financial Analysis & Decision Support", from: 10, to: 15 },
      { group: "Operational Finance", from: 15, to: 20 },
      { group: "Leadership & Communication", from: 20, to: 25 },
    ]);
  }

  if (fallback || roles.includes("Operations")) {
    addSkillGroup(operationsSkillList, [
      { group: "Strategic Operations Management", from: 0, to: 5 },
      { group: "Process Optimization", from: 5, to: 10 },
      { group: "Supply Chain & Logistics", from: 10, to: 15 },
      { group: "People Management", from: 15, to: 20 },
      { group: "Financial & Resource Management", from: 20, to: 25 },
    ]);
  }

  if (fallback || roles.includes("Marketing")) {
    addSkillGroup(marketingSkillList, [
      { group: "Strategic Marketing Management", from: 0, to: 5 },
      { group: "Digital Marketing", from: 5, to: 10 },
      { group: "Creative Direction", from: 10, to: 15 },
      { group: "Communication & Public Relations", from: 15, to: 20 },
      { group: "Analytics & Insights", from: 20, to: 25 },
    ]);
  }

  if (fallback || roles.includes("Sales")) {
    addSkillGroup(salesSkillList, [
      { group: "Strategic Sales Leadership", from: 0, to: 5 },
      { group: "Business Development", from: 5, to: 10 },
      { group: "Sales Execution & Techniques", from: 10, to: 15 },
      { group: "Sales Team Management", from: 15, to: 20 },
      { group: "Analytics and Technology", from: 20, to: 25 },
    ]);
  }

  if (
    fallback ||
    roles.includes("Software Dev") ||
    roles.includes("Information Security")
  ) {
    addSkillGroup(softwareEngineeringSkillList, [
      { group: "Strategic Technology Leadership", from: 0, to: 5 },
      { group: "Software Engineering", from: 5, to: 10 },
      { group: "Product Management", from: 10, to: 15 },
      { group: "Technical Skill Proficiency", from: 15, to: 20 },
      { group: "Team and Resource Management", from: 20, to: 25 },
    ]);
  }

  if (fallback || roles.includes("Product")) {
    addSkillGroup(productSkillList, [
      { group: "Product Strategy and Vision", from: 0, to: 5 },
      { group: "Product Development", from: 5, to: 10 },
      { group: "User Experience and Design", from: 10, to: 15 },
      { group: "Customer and Market Insights", from: 15, to: 20 },
      {
        group: "Leadership and Cross-Functional Collaboration",
        from: 20,
        to: 25,
      },
    ]);
  }

  if (
    fallback ||
    roles.includes("Data and Analytics") ||
    roles.includes("Information Security")
  ) {
    addSkillGroup(dataAndAnalyticsSkillList, [
      { group: "Data Strategy and Management", from: 0, to: 5 },
      { group: "Analytics and Business Intelligence", from: 5, to: 10 },
      {
        group: "Machine Learning and Artificial Intelligence",
        from: 10,
        to: 15,
      },
      { group: "Data Integration and Engineering", from: 15, to: 20 },
      {
        group: "Leadership and Organizational Development",
        from: 20,
        to: 25,
      },
    ]);
  }

  if (fallback || roles.includes("HR")) {
    addSkillGroup(hrSkillList, [
      { group: "Strategic HR Leadership", from: 0, to: 5 },
      { group: "Talent Management", from: 5, to: 10 },
      { group: "Employee Relations and Engagement", from: 10, to: 15 },
      { group: "HR Operations and Administration", from: 15, to: 20 },
      { group: "Learning and Development", from: 20, to: 25 },
    ]);
  }

  return { topOptions, secondaryOptions, additionalOptions, optionsCount };
};

export default CandidateCompetenciesEditFormDialog;
